import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

export const _frontmatter = {
  "title": "Land and Water Conservation Fund | How revenue works",
  "includeToc": true,
  "description": "Congress created the Land and Water Conservation Fund (LWCF) in 1964. The fund went into effect on January 1, 1965. It supports preservation, development, and access to outdoor lands for public recreation. The LWCF is funded by revenue from offshore oil and gas leases.",
  "tags": ["How revenue works", "offshore", "conservation", "Gulf of Mexico", "GOMESA", "disbursements", "recreation", "grants", "National Park Service"]
};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const GlossaryTerm = makeShortcode("GlossaryTerm");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><a parentName="p" {...{
        "href": "/sites/About-Page-Updates/how-revenue-works/"
      }}>{`How revenue works /`}</a></p>
    <h1 {...{
      "id": "Land-and-Water-Conservation-Fund",
      "style": {
        "position": "relative"
      }
    }}>{`Land and Water Conservation Fund`}</h1>
    <p>{`Congress created the Land and Water Conservation Fund (LWCF) in 1964. The fund went into effect on January 1, 1965. It supports preservation, development, and access to outdoor lands for public recreation. The LWCF is funded by revenue from offshore oil and gas leases. `}<br /><br /></p>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://www.nps.gov/subjects/lwcf/index.htm"
      }}>{`Land and Water Conservation Fund`}</a>{` provides competitive grants to states for local preservation and recreation projects. The LWCF also contains a federal portion to acquire lands and waters for public recreation or cultural resource preservation.`}</p>
    <h2 {...{
      "id": "Funding-priorities",
      "style": {
        "position": "relative"
      }
    }}>{`Funding priorities`}</h2>
    <p>{`Depending on the agency or program, the LWCF supports:`}</p>
    <ul>
      <li parentName="ul">{`increased access to state and locally owned outdoor recreation opportunities`}</li>
      <li parentName="ul">{`creation or expansion of national trails, parks, and forests`}</li>
      <li parentName="ul">{`preservation of working lands (e.g. private farms, forests, and ranches)`}</li>
      <li parentName="ul">{`preservation of historic or cultural sites`}</li>
      <li parentName="ul">{`development or rehabilitation of urban parks`}</li>
      <li parentName="ul">{`conservation of wildlife habitat`}</li>
    </ul>
    <h2 {...{
      "id": "Authorization-and-funding",
      "style": {
        "position": "relative"
      }
    }}>{`Authorization and funding`}</h2>
    <p>{`On March 12, 2019, the President signed into law the `}<a parentName="p" {...{
        "href": "https://www.congress.gov/bill/116th-congress/senate-bill/47"
      }}>{`John D. Dingell, Jr. Conservation, Management, and Recreation Act`}</a>{` (formerly the Natural Resources Management Act). This bill reauthorizes the Land and Water Conservation Fund and also permanently `}<GlossaryTerm mdxType="GlossaryTerm">{`authorizes`}</GlossaryTerm>{` the fund, which means the fund no longer needs to be reauthorized by Congress in order to receive funding. Until the passage of the `}<a parentName="p" {...{
        "href": "https://www.congress.gov/bill/116th-congress/house-bill/1957"
      }}>{`Great American Outdoors Act`}</a>{`, Congress still needed to `}<GlossaryTerm mdxType="GlossaryTerm">{`appropriate`}</GlossaryTerm>{` funds for the program during the annual budget process in order for the LWCF to receive funds. `}<br /><br /></p>
    <p>{`The LWCF is authorized to receive and distribute up to $900 million each year.`}</p>
    <h3 {...{
      "id": "Annual-funding",
      "style": {
        "position": "relative"
      }
    }}>{`Annual funding`}</h3>
    <p>{`The fund doesn't receive any tax revenue, but is instead funded largely by offshore oil and gas revenue. `}<GlossaryTerm mdxType="GlossaryTerm">{`ONRR`}</GlossaryTerm>{` disburses that revenue to the fund each year. `}<br /><br /></p>
    <p>{`On August 4, 2020, the President signed the `}<a parentName="p" {...{
        "href": "https://www.congress.gov/bill/116th-congress/house-bill/1957"
      }}>{`Great American Outdoors Act`}</a>{` bill into law. The Act permanently and fully funds the LWCF at the full amount deposited into the fund, beginning with amounts deposited during Fiscal Year 2020. While this Act means that annual appropriations are no longer necessary, the fund could still receive less than $900 million a year if its funding source does not result in enough revenues to meet that limit.`}</p>
    <h2 {...{
      "id": "Distribution-of-funds",
      "style": {
        "position": "relative"
      }
    }}>{`Distribution of funds`}</h2>
    <p>{`Most of the funds from the LWCF support two general purposes:`}</p>
    <ul>
      <li parentName="ul">{`Federal acquisition of land for the benefit of the public.`}</li>
      <li parentName="ul">{`Grants to states for recreational planning, acquiring recreational lands, or developing outdoor recreational facilities.`}</li>
    </ul>
    <p>{`A portion of funds also supports:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.nps.gov/orgs/2287/index.htm"
        }}>{`American Battlefield Protection Program`}</a>{`. This program sponsors the preservation of historic battlefields.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.fs.usda.gov/managing-land/private-land/forest-legacy/program"
        }}>{`Forest Legacy Program`}</a>{`. This is a Forest Service program that encourages the protection of privately owned forest lands.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.fws.gov/endangered/grants/"
        }}>{`Cooperative Endangered Species Conservation Fund`}</a>{`. This is a Fish and Wildlife Service program that provides funding to states and territories for species and habitat conservation on non-federal lands.`}</li>
    </ul>
    <p>{`Every year during the budget process, Congress determines the total appropriation from the fund to support each of these purposes.`}</p>
    <h3 {...{
      "id": "Federal-land-acquisition",
      "style": {
        "position": "relative"
      }
    }}>{`Federal land acquisition`}</h3>
    <p>{`Federal land management agencies include the Forest Service, National Park Service, Fish and Wildlife Service, and the Bureau of Land Management. `}<br /><br /></p>
    <p>{`During the annual budget process, these agencies can submit proposals to acquire lands or waters with LWCF funds. LWCF funds for land acquisition can be carried over from fiscal year to fiscal year until expended. `}<br /><br /></p>
    <p><a parentName="p" {...{
        "href": "https://www.nps.gov/subjects/lwcf/land-acquisition-reports-and-data.htm"
      }}>{`Review land acquisition reports and data`}</a>{`.`}</p>
    <h3 {...{
      "id": "State-and-local-grant-funding",
      "style": {
        "position": "relative"
      }
    }}>{`State and local grant funding`}</h3>
    <p>{`Administered by the National Park Service, the stateside program of the LWCF provides matching grants to states for planning, acquiring lands and waters, and developing facilities for outdoor recreation. LWCF funds are limited to 50% or less of a proposed project's total cost; the rest must come from other, non-federal funds. `}<br /><br /></p>
    <p>{`A formula in the Land and Water Conservation Act determines how the funds are apportioned to states (See `}<a parentName="p" {...{
        "href": "https://www.gpo.gov/fdsys/granule/USCODE-2014-title54/USCODE-2014-title54-subtitleII-chap2003-sec200305"
      }}>{`54 U.S.C. §200305(b)`}</a>{`). States must use the money for its intended purpose within three years. `}<br /><br /></p>
    <p><a parentName="p" {...{
        "href": "https://www.nps.gov/subjects/lwcf/statefundingstatus.htm"
      }}>{`Review state and local grant funding status`}</a>{`.`}</p>
    <h4 {...{
      "id": "GOMESA-grants",
      "style": {
        "position": "relative"
      }
    }}>{`GOMESA grants`}</h4>
    <p>{`The Gulf of Mexico Energy Security Act (GOMESA) of 2006 specifies that 12.5% of revenues from certain gulf leases be directed to the LWCF stateside program. States can receive up to $125 million a year in funding. This portion of LWCF funds is not subject to the congressional appropriation process, but are otherwise treated similarly to regular LWCF funds.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      